const filterNonWhitespaceNodes = (nodes: Node[]) => {
	return nodes.filter(
		(node) => node.nodeName !== '#text' || (node.nodeValue && node.nodeValue.replace(/[\n\r\t]/g, '').length > 0),
	);
};

/**
 * Return elements in a given slot
 *
 * @param slotElement The <slot> element to get nodes of
 */
export const getSlottedElements = (slotElement: HTMLSlotElement): HTMLElement[] => {
	// Get directly slotted elements (can include other slots)
	const singleLevelElements = filterNonWhitespaceNodes(slotElement.assignedNodes()) as HTMLElement[];

	// Get all slotted elements, including from multiple levels of slots
	const multiLevelElements: HTMLElement[] = [];
	singleLevelElements.forEach((element) => {
		if (element.nodeName === 'SLOT') {
			multiLevelElements.push(...getSlottedElements(element as HTMLSlotElement));
		} else {
			multiLevelElements.push(element);
		}
	});

	return multiLevelElements;
};

/**
 * Returns whether a slot of the given host element is filled.
 * Also takes into account slots that as passed on to sub-components.
 *
 * @param {Element} hostElement - The shadow root host element that the slot belongs to
 * @param {string} [slotName] - The name of the slot to search for. If not specified, all slots are checked
 * @returns {boolean} - True if the element has the specified slot assigned, false otherwise
 */
export function hasSlotted(hostElement: Element, slotName?: string): boolean {
	if (!slotName) {
		// No slot specified, so check all child nodes
		const slottedElements = filterNonWhitespaceNodes(Array.from(hostElement.childNodes));
		return slottedElements.length > 0;
	}

	// Query for the first direct child element of hostElement that has a slot attribute with the value of slotName
	const slottedElements: HTMLElement[] = Array.from(hostElement.querySelectorAll(`:scope > [slot="${slotName}"]`));

	return !!slottedElements.find((slottedElement) => isElementWithContent(slottedElement));
}

/** Returns true if this is a non-slot node, or a slot filled with content */
const isElementWithContent = (element: HTMLElement): boolean => {
	if (element.localName !== 'slot') return true;

	const assignedElements = filterNonWhitespaceNodes((element as HTMLSlotElement).assignedElements());
	return !!assignedElements.find((assignedElement) => isElementWithContent(assignedElement as HTMLElement));
};

/**
 * Returns whether a given target element is slotted in a particular host element.
 * Also takes into account slots that as passed on to sub-components.
 *
 * @param {Element} targetElement - The element to look for
 * @param {Element} hostElement - The shadow root host element that the slot belongs to
 * @param {string} [slotName] - The name of the slot to search for. If not specified, all slots are checked
 * @returns {boolean} - True if the element has the specified slot assigned, false otherwise
 */
export function isSlottedInto(targetElement: Element, hostElement: Element, slotName?: string): boolean {
	if (!slotName) {
		// No slot specified, so check all child nodes
		const slottedNodes = Array.from(hostElement.childNodes);
		return slottedNodes.includes(targetElement);
	}

	// Query for the first direct child element of hostElement that has a slot attribute with the value of slotName
	const slottedElement: HTMLElement | null = hostElement.querySelector(`:scope > [slot="${slotName}"]`);
	if (slottedElement === targetElement) {
		return true;
	}

	if (slottedElement && slottedElement.nodeName.toLowerCase() === 'slot') {
		// The slotted element is a slot itself, so check its slotted content
		const assignedNodes = (slottedElement as HTMLSlotElement).assignedNodes();
		return assignedNodes?.includes(targetElement);
	}

	return false;
}
